import { useState, useEffect, useCallback } from 'react';
import { useAppForeground } from '@/hooks/useAppForeground';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowDown, Check, ShieldCheck, Zap, Landmark, ChevronRight, Loader2, ChevronLeft, Clock } from 'lucide-react';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import WalletShell from '@/components/wallet/WalletShell';
import WalletActivityRow from '@/components/wallet/WalletActivityRow';
import NFLFundsSection from '@/components/nfl/NFLFundsSection';
import { mapActivity, cleanBankLabel, type WalletTxnRow, type ActivityView } from '@/lib/walletActivity';
import { launchPersonaInquiry, recheckKycGate } from '@/lib/personaKyc';
import { cn } from '@/lib/utils';

// SAB-565 pt 2: Wallet home. Source of truth: design-handoff/wallet-home/README.md.
// Renders under the app's real chrome via WalletShell (review fix 1). Withdrawal +
// bank-management LOGIC reused as-is from NFLFundsSection (drill-in via `showFunds`).

const fmtUSD = (c: number) => (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (c: number) => (c / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: c % 100 ? 2 : 0 });
const eyebrow = 'text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-medium';
const PERIOD_WORD: Record<string, string> = { daily: 'today', weekly: 'this week', monthly: 'this month' };

interface Limits { period: 'daily' | 'weekly' | 'monthly'; limitCents: number; usedCents: number; }

function WalletInner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentLeague, leagues, isLoading } = useNFLLeague();

  const [loading, setLoading] = useState(true);
  const [balanceCents, setBalanceCents] = useState(0);
  const [pendingCents, setPendingCents] = useState(0);
  const [banks, setBanks] = useState<{ id: string; name: string; last4: string; isDefault: boolean }[]>([]);
  const [txns, setTxns] = useState<WalletTxnRow[]>([]);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  const [kycVerifying, setKycVerifying] = useState(false);
  const [showFunds, setShowFunds] = useState(false);

  const kycApproved = kycStatus === 'approved';
  const canWithdraw = balanceCents > 0;

  const load = useCallback(async () => {
    if (!user) return;
    if (currentLeague) {
      const { data: w } = await supabase.from('user_wallets').select('balance, pending_deposits').eq('user_id', user.id).maybeSingle();
      setBalanceCents(Math.round(Number((w as any)?.balance ?? 0) * 100));
      setPendingCents(Math.round(Number((w as any)?.pending_deposits ?? 0) * 100));
    }
    // No is_default column — the first-linked active bank is the default. Fix 3: sanitize the label.
    const { data: b } = await supabase.from('linked_bank_accounts').select('id, bank_name, account_last_four, status, linked_at').eq('user_id', user.id).eq('status', 'active').order('linked_at', { ascending: true });
    setBanks(((b as any[]) || []).map((x, i) => ({ id: x.id, name: cleanBankLabel(x.bank_name), last4: x.account_last_four || '', isDefault: i === 0 })));
    // Own-row activity via RLS (auth.uid() = user_id). Unified feed across leagues.
    const { data: t } = await supabase.from('wallet_transactions').select('id, type, amount, status, metadata, description, reference_id, league_id, created_at').eq('user_id', user.id).order('created_at', { ascending: false }).limit(12);
    setTxns((t as any[]) || []);
    const { data: lim } = await supabase.rpc('get_deposit_limit_status', { p_user_id: user.id });
    if (lim) { const p = (lim as any).tightest_period; const d = (lim as any)[p]; setLimits({ period: p, limitCents: d.limit_cents, usedCents: d.used_cents }); }
    const { data: prof } = await supabase.from('profiles').select('kyc_status').eq('id', user.id).maybeSingle();
    setKycStatus((prof as any)?.kyc_status ?? null);
    setLoading(false);
  }, [user, currentLeague]);
  useEffect(() => { if (!isLoading) load(); }, [load, isLoading]);

  // SAB-979: the wallet is the surface a returning depositor looks at first. The credit
  // lands via the PayPal webhook with no app involvement, so without this the user is shown
  // the pre-deposit balance until they navigate away and back. Not react-query, so
  // ForegroundRefetch's invalidateQueries does not reach it.
  useAppForeground(load);

  // Return-landing from a Soap card withdrawal. The debit settles server-side on
  // checkout.hold; load() above already refetches the balance, so here we just strip
  // the marker so it does not linger in the URL.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('soap_wd_return') !== '1') return;
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // Live update while anything is in-flight: poll so pending rows transition in place.
  const hasPending = txns.some((t) => t.status !== 'completed' && t.status !== 'settled' && t.status !== 'succeeded' && t.status !== 'failed');
  useEffect(() => {
    if (!hasPending) return;
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, [hasPending, load]);

  const startKyc = async (onDone?: () => void) => {
    if (!user || kycVerifying) return;
    setKycVerifying(true);
    try {
      await launchPersonaInquiry({
        tier: 1, userId: user.id,
        onComplete: async () => {
          const ok = await recheckKycGate(user.id, 1);
          setKycVerifying(false);
          if (ok) { setKycStatus('approved'); await load(); onDone?.(); }
        },
        onCancel: () => setKycVerifying(false),
        onError: () => setKycVerifying(false),
      });
    } catch { setKycVerifying(false); }
  };

  const onWithdraw = () => { if (canWithdraw) setShowFunds(true); };

  const bankMap: Record<string, string> = {};
  banks.forEach((b) => { bankMap[b.id] = `${b.name}${b.last4 ? ` ••${b.last4}` : ''}`; });
  const leagueMap: Record<string, string> = {};
  (leagues || []).forEach((l: any) => { leagueMap[l.id] = l.name; });
  const views: ActivityView[] = txns.map((r) => mapActivity(r, bankMap, leagueMap));

  if (showFunds) {
    return (
      <div className="mx-auto w-full max-w-[1000px] px-[22px] pt-4 pb-[30px] lg:px-8 lg:pt-9">
        <button onClick={() => { setShowFunds(false); load(); }} className="flex items-center gap-[5px] text-[15px] font-medium text-muted-foreground mb-3 hover:text-foreground">
          <ChevronLeft className="h-5 w-5" strokeWidth={2.2} /> Back to Wallet
        </button>
        <NFLFundsSection hideDeposit />
      </div>
    );
  }

  const panelProps = { balanceCents, pendingCents, kycApproved, kycVerifying, canWithdraw, loading, onDeposit: () => navigate('/wallet/deposit'), onWithdraw, onVerify: () => startKyc() };

  return (
    <>
      {/* ============================ DESKTOP ============================ */}
      <div className="hidden lg:block mx-auto w-full max-w-[1000px] px-8 pt-9 pb-14">
        <div className="flex items-end justify-between mb-[26px]">
          <h1 className="font-display font-extrabold text-[34px] tracking-[-0.02em] text-foreground leading-none">Wallet</h1>
          <button onClick={() => navigate('/wallet/history')} className="text-[14px] font-semibold text-primary hover:text-primary/80">Transaction history</button>
        </div>
        <div className="grid grid-cols-[1fr_372px] gap-7 items-start">
          <div className="flex flex-col gap-[22px]">
            <BalancePanel {...panelProps} />
            <RecentActivity views={views} count={4} loading={loading} onSeeAll={() => navigate('/wallet/history')} />
          </div>
          <div className="flex flex-col gap-[18px]">
            {limits && <LimitsCard limits={limits} onRG={() => navigate('/responsible-gaming')} />}
            <CurrencyNote />
          </div>
        </div>
      </div>

      {/* ============================ MOBILE ============================ */}
      <div className="lg:hidden px-[22px] pt-[14px] pb-[26px]">
        <div className="flex items-center justify-between mb-5">
          <h1 className="font-display font-extrabold text-[30px] tracking-[-0.02em] text-foreground leading-none">Wallet</h1>
          <button onClick={() => navigate('/wallet/history')} aria-label="Transaction history" className="h-[38px] w-[38px] rounded-full grid place-items-center bg-card border border-border">
            <Clock className="h-[18px] w-[18px] text-muted-foreground" />
          </button>
        </div>
        <div className="flex flex-col gap-[18px]">
          <BalancePanel mobile {...panelProps} />
          {limits && <LimitsCard mobile limits={limits} onRG={() => navigate('/responsible-gaming')} />}
          <RecentActivity mobile views={views} count={3} loading={loading} onSeeAll={() => navigate('/wallet/history')} />
          <CurrencyNote />
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────── Balance panel ─────────────────────────── */
function BalancePanel({ balanceCents, pendingCents, kycApproved, kycVerifying, canWithdraw, loading, mobile, onDeposit, onWithdraw, onVerify }: {
  balanceCents: number; pendingCents: number; kycApproved: boolean; kycVerifying: boolean; canWithdraw: boolean; loading: boolean; mobile?: boolean;
  onDeposit: () => void; onWithdraw: () => void; onVerify: () => void;
}) {
  const withdrawDisabled = !canWithdraw;
  return (
    <div className={cn('relative overflow-hidden border border-border rounded-[20px]', mobile ? 'p-[24px_22px]' : 'p-[30px]')} style={{ background: 'var(--gradient-card)' }}>
      <span className="absolute rounded-full pointer-events-none" style={{ top: mobile ? -80 : -90, right: mobile ? -50 : -60, width: mobile ? 210 : 260, height: mobile ? 210 : 260, background: 'hsl(25 95% 53% / 0.12)', filter: `blur(${mobile ? 55 : 60}px)` }} />
      <div className="relative">
        <p className={cn(eyebrow, 'mb-3')}>Withdrawable balance</p>
        <div className="flex items-end gap-[14px]">
          {loading ? (
            <div className={cn('animate-pulse rounded-md bg-muted', mobile ? 'h-[44px] w-[180px]' : 'h-[54px] w-[220px]')} />
          ) : (
            <span className={cn('font-display font-extrabold text-foreground tabular-nums leading-none', mobile ? 'text-[44px]' : 'text-[54px]')} style={{ letterSpacing: '-0.03em' }}>${fmtUSD(balanceCents)}</span>
          )}
          {!loading && (kycApproved ? (
            <span className="inline-flex items-center gap-[5px] mb-[7px] text-[12.5px] rounded-full px-[10px] py-1" style={{ color: 'hsl(160 84% 39%)', background: 'hsl(160 84% 39% / 0.12)', border: '1px solid hsl(160 84% 39% / 0.3)' }}>
              <Check className="h-3 w-3" strokeWidth={2.4} /> Verified
            </span>
          ) : (
            <button onClick={onVerify} disabled={kycVerifying} className="inline-flex items-center gap-[5px] mb-[7px] text-[12.5px] rounded-full px-[10px] py-1 text-primary hover:bg-primary/10 disabled:opacity-60" style={{ background: 'hsl(25 95% 53% / 0.12)', border: '1px solid hsl(25 95% 53% / 0.3)' }}>
              {kycVerifying ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" strokeWidth={2.4} />} Verify identity
            </button>
          ))}
        </div>
        {pendingCents > 0 && <p className={cn('text-muted-foreground mt-2', mobile ? 'text-[13px]' : 'text-[13.5px]')}>${fmtUSD(pendingCents)} pending · clears in 1–3 days</p>}
        <div className={cn('flex mt-5', mobile ? 'gap-[10px]' : 'gap-3')}>
          <button onClick={onDeposit} className={cn('flex-1 rounded-full bg-primary text-white font-display font-bold text-[16px] flex items-center justify-center gap-2 hover:bg-primary/90', mobile ? 'h-[52px]' : 'h-[54px]')} style={{ boxShadow: 'var(--shadow-sabotage)' }}>
            <Plus className="h-[17px] w-[17px]" strokeWidth={2.4} /> Deposit
          </button>
          <span className="flex-1" title={withdrawDisabled ? 'Nothing to withdraw yet' : undefined}>
            <button onClick={onWithdraw} disabled={withdrawDisabled} className={cn('w-full rounded-full border border-border bg-transparent text-foreground font-display font-bold text-[16px] flex items-center justify-center gap-2 hover:border-muted-foreground', mobile ? 'h-[52px]' : 'h-[54px]', withdrawDisabled && 'opacity-45 pointer-events-none')}>
              <ArrowDown className="h-[17px] w-[17px]" strokeWidth={2.2} /> Withdraw
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Recent activity ─────────────────────────── */
function RecentActivity({ views, count, loading, mobile, onSeeAll }: { views: ActivityView[]; count: number; loading: boolean; mobile?: boolean; onSeeAll: () => void }) {
  const shown = views.slice(0, count);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <p className={eyebrow}>Recent activity</p>
        <button onClick={onSeeAll} className="text-[13px] font-semibold text-primary hover:text-primary/80">See all</button>
      </div>
      <div className="rounded-[16px] border border-border bg-card overflow-hidden divide-y divide-border">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={cn('flex items-center', mobile ? 'gap-3 px-[15px] py-[14px]' : 'gap-[14px] px-[18px] py-[15px]')}>
              <div className="h-[38px] w-[38px] rounded-[11px] bg-muted animate-pulse shrink-0" />
              <div className="flex-1 space-y-1.5"><div className="h-[13px] w-2/5 bg-muted rounded animate-pulse" /><div className="h-[11px] w-1/4 bg-muted rounded animate-pulse" /></div>
              <div className="h-[14px] w-16 bg-muted rounded animate-pulse" />
            </div>
          ))
        ) : shown.length === 0 ? (
          <p className="text-[13.5px] text-muted-foreground text-center px-6 py-8">No activity yet. Your deposits and payouts will show up here.</p>
        ) : (
          shown.map((v) => <WalletActivityRow key={v.id} v={v} mobile={mobile} />)
        )}
      </div>
    </div>
  );
}

/* SAB-1120: the Payment methods card is GONE, not hidden behind a flag.
   It only ever listed linked bank accounts, and the Aeropay/Aerosync rail it
   managed was retired by SAB-767 -- aeropay-connect-bank answers 410 RAIL_RETIRED.
   Both its buttons ("Manage" and "Add a method") called setShowFunds(true), so
   they were the same control twice, and both led to a screen whose only action
   could not complete. Soap card payout is chosen at withdrawal time and is not a
   stored method, so there is nothing left for this card to show. `banks` is still
   loaded above: it names bank rows in the activity feed (bankMap), which must keep
   rendering for historical ACH transactions. */

/* ─────────────────────────── Deposit limits (rail / strip) ─────────────────────────── */
function LimitsCard({ limits, mobile, onRG }: { limits: Limits; mobile?: boolean; onRG: () => void }) {
  const remaining = Math.max(limits.limitCents - limits.usedCents, 0);
  const pct = limits.limitCents > 0 ? Math.min(100, Math.round((limits.usedCents / limits.limitCents) * 100)) : 0;
  const word = PERIOD_WORD[limits.period];
  return (
    <div className={cn('border border-border bg-card', mobile ? 'rounded-[14px] p-[15px_16px]' : 'rounded-[16px] p-5')}>
      {mobile ? <p className="text-[14px] text-muted-foreground mb-2.5">Deposited {word}</p> : <p className={cn(eyebrow, 'mb-3.5')}>Deposit limits</p>}
      <div className="flex items-baseline justify-between">
        {!mobile && <span className="text-[14px] text-muted-foreground capitalize">{word}</span>}
        <span className={cn('font-display font-bold text-[14px] text-foreground', mobile && 'ml-auto')}>${fmtMoney(limits.usedCents)}<span className="text-muted-foreground/70 font-medium"> / ${fmtMoney(limits.limitCents)}</span></span>
      </div>
      <div className={cn('rounded-full bg-muted overflow-hidden mt-2 mb-[7px]', mobile ? 'h-[7px]' : 'h-2')}><div className="h-full rounded-full bg-gold" style={{ width: `${pct}%` }} /></div>
      <p className="text-[12px] font-semibold text-gold">${fmtMoney(remaining)} left {word}</p>
      {!mobile && (
        <>
          <div className="border-t border-border mt-3.5 mb-3.5" />
          <button onClick={onRG} className="flex items-center gap-[9px] w-full text-left">
            <span className="flex-1">
              <span className="block text-[13.5px] font-semibold text-primary">Responsible Gaming settings</span>
              <span className="block text-[11.5px] text-muted-foreground/70">Increases take effect after 24 hours.</span>
            </span>
            <ChevronRight className="h-[15px] w-[15px] text-muted-foreground/60" />
          </button>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────── Currency note (rail) ─────────────────────────── */
function CurrencyNote() {
  return (
    <div className="rounded-[14px] border border-border bg-transparent p-[15px_16px] flex gap-[11px]">
      <Zap className="h-4 w-4 text-primary shrink-0" style={{ marginTop: 1 }} />
      <p className="text-[12.5px] leading-[1.5] text-muted-foreground/70">
        Balance is real money — cash out anytime. <span className="text-muted-foreground font-semibold">FAAB</span> and <span className="text-muted-foreground font-semibold">Sabotage Points</span> are in-game only.
      </p>
    </div>
  );
}

export default function Wallet() {
  return (
    <WalletShell>
      <WalletInner />
    </WalletShell>
  );
}
